import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Cake, Sparkles, X, RefreshCw, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` });

const AdminLoyalty = () => {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/loyalty${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers: authHeader() });
      setCustomers(data || []);
    } catch (e) { toast.error('Erreur de chargement'); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    try {
      await axios.post(`${API}/admin/loyalty`, payload, { headers: authHeader() });
      toast.success('Client ajouté');
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    }
  };

  const updatePoints = async (id, delta, amount, note) => {
    try {
      await axios.patch(`${API}/admin/loyalty/${id}/points`,
        { delta_points: delta, amount_spent: amount || null, note: note || null },
        { headers: authHeader() });
      toast.success('Points mis à jour');
      setEditing(null);
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try {
      await axios.delete(`${API}/admin/loyalty/${id}`, { headers: authHeader() });
      toast.success('Supprimé');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const [syncing, setSyncing] = useState(false);
  const syncCustomers = async () => {
    if (!window.confirm('Importer tous les clients depuis Square ? Les emails existants seront mis à jour.')) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/loyalty/sync-square-customers`, {}, { headers: authHeader() });
      toast.success(`✅ ${data.created} créés · ${data.updated} mis à jour · ${data.skipped_no_email} sans email`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sync Square');
    } finally { setSyncing(false); }
  };
  const syncPayments = async () => {
    if (!window.confirm('Créditer automatiquement les points des paiements Square des 30 derniers jours ?')) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/loyalty/sync-square-payments?days=30`, {}, { headers: authHeader() });
      toast.success(`💰 ${data.processed} paiements · ${data.points_credited} pts crédités · ${data.skipped} skippés`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sync paiements');
    } finally { setSyncing(false); }
  };

  const currentMonth = new Date().getMonth() + 1;
  const birthdaysThisMonth = customers.filter((c) => c.birthday && parseInt(c.birthday.split('-')[1], 10) === currentMonth);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-loyalty">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
            Cercle Harmony · Fidélité
          </h1>
          <p className="text-sm text-[#332724]/70 mt-1">{customers.length} client(s) inscrit(s)</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={syncCustomers}
            disabled={syncing}
            className="bg-[#1E7A6A] hover:bg-[#155a4e] text-[#FDFAF4] px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.15em] inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="loyalty-sync-customers-btn"
          >
            <RefreshCw size={13} /> Importer clients Square
          </button>
          <button
            onClick={syncPayments}
            disabled={syncing}
            className="bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.15em] inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="loyalty-sync-payments-btn"
          >
            <CreditCard size={13} /> Créditer paiements Square (30j)
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="btn-primary px-5 py-2 rounded-full text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2"
            data-testid="loyalty-add-btn"
          >
            <Plus size={14} /> Ajouter manuellement
          </button>
        </div>
      </div>

      {/* Anniversaires du mois */}
      {birthdaysThisMonth.length > 0 && (
        <div className="bg-gradient-to-r from-[#B8922A]/10 to-white border-2 border-[#B8922A] rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Cake size={20} className="text-[#B8922A]" />
            <h2 className="text-lg text-[#5C2018] font-medium">
              🎂 Anniversaires de ce mois ({birthdaysThisMonth.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {birthdaysThisMonth.map((c) => (
              <div key={c.id} className="bg-white rounded-lg p-3 border border-[#B8922A]/30">
                <p className="font-medium text-[#5C2018]">{c.name}</p>
                <p className="text-xs text-[#332724]/70">{c.email}</p>
                <p className="text-xs text-[#B8922A] mt-1">📅 {c.birthday}</p>
                <p className="text-xs text-[#1E7A6A] italic mt-1">→ Envoyer le code -20%</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recherche */}
      <div className="bg-white rounded-2xl p-4 border border-[#B8922A]/20 mb-4 flex items-center gap-3">
        <Search size={18} className="text-[#B8922A]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher par nom, email ou téléphone…"
          className="flex-1 outline-none text-sm"
          data-testid="loyalty-search"
        />
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-2xl border border-[#B8922A]/20 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFAF4] border-b border-[#B8922A]/15">
            <tr>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-[#5C2018]">Client</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-[#5C2018]">Contact</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-[#5C2018]">Anniv.</th>
              <th className="text-right p-3 text-xs uppercase tracking-[0.15em] text-[#5C2018]">Points</th>
              <th className="text-right p-3 text-xs uppercase tracking-[0.15em] text-[#5C2018]">Cumul</th>
              <th className="text-center p-3 text-xs uppercase tracking-[0.15em] text-[#5C2018]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && (
              <tr><td colSpan="6" className="p-8 text-center text-[#332724]/60 italic">Aucun client pour le moment. Cliquez sur « Ajouter un client » pour commencer.</td></tr>
            )}
            {customers.map((c) => {
              const isBirthdayMonth = c.birthday && parseInt(c.birthday.split('-')[1], 10) === currentMonth;
              return (
                <tr key={c.id} className="border-b border-[#B8922A]/10 hover:bg-[#FDFAF4]/50" data-testid={`loyalty-row-${c.id}`}>
                  <td className="p-3">
                    <p className="font-medium text-[#5C2018]">{c.name}</p>
                    {c.notes && <p className="text-[11px] text-[#332724]/60 italic">{c.notes}</p>}
                  </td>
                  <td className="p-3 text-xs text-[#332724]">
                    {c.email}
                    {c.phone && <><br /><span className="text-[#332724]/70">{c.phone}</span></>}
                  </td>
                  <td className="p-3 text-xs">
                    {c.birthday ? (
                      <span className={isBirthdayMonth ? 'text-[#B8922A] font-medium' : 'text-[#332724]/70'}>
                        {isBirthdayMonth && '🎂 '}{c.birthday}
                      </span>
                    ) : <span className="text-[#332724]/40">—</span>}
                  </td>
                  <td className="p-3 text-right">
                    <span className="text-lg font-medium text-[#1E7A6A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                      {c.points}
                    </span>
                  </td>
                  <td className="p-3 text-right text-xs text-[#332724]/70">
                    {Number(c.total_spent || 0).toFixed(0)}€
                  </td>
                  <td className="p-3 text-center">
                    <button
                      onClick={() => setEditing(c)}
                      className="text-[#1E7A6A] hover:text-[#5C2018] mr-3 text-xs underline"
                      data-testid={`loyalty-edit-${c.id}`}
                    >
                      Créditer
                    </button>
                    <button
                      onClick={() => del(c.id, c.name)}
                      className="text-[#5C2018]/60 hover:text-[#5C2018]"
                      aria-label="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modale ajout */}
      {showAdd && (
        <AddCustomerModal onClose={() => setShowAdd(false)} onSubmit={create} />
      )}

      {/* Modale crédit */}
      {editing && (
        <AddPointsModal customer={editing} onClose={() => setEditing(null)} onSubmit={updatePoints} />
      )}
    </div>
  );
};

const AddCustomerModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', birthday: '', points: 0, notes: '' });
  return (
    <Modal onClose={onClose} title="Nouveau client fidélité">
      <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...form, points: parseInt(form.points || 0, 10) }); }} className="space-y-3">
        <input required placeholder="Nom complet *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-2.5 border border-[#B8922A]/30 rounded-lg text-sm" data-testid="loyalty-form-name" />
        <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full p-2.5 border border-[#B8922A]/30 rounded-lg text-sm" data-testid="loyalty-form-email" />
        <input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full p-2.5 border border-[#B8922A]/30 rounded-lg text-sm" />
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-[#332724]/70">Anniversaire (jj/mm)</label>
          <input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} className="w-full p-2.5 border border-[#B8922A]/30 rounded-lg text-sm mt-1" data-testid="loyalty-form-birthday" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-[#332724]/70">Points de bienvenue (optionnel)</label>
          <input type="number" min="0" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className="w-full p-2.5 border border-[#B8922A]/30 rounded-lg text-sm mt-1" />
        </div>
        <textarea placeholder="Notes (préférences, infos…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full p-2.5 border border-[#B8922A]/30 rounded-lg text-sm resize-none" />
        <button type="submit" className="w-full btn-primary px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.2em]" data-testid="loyalty-form-submit">
          Créer le client
        </button>
      </form>
    </Modal>
  );
};

const AddPointsModal = ({ customer, onClose, onSubmit }) => {
  const [amount, setAmount] = useState('');
  const [delta, setDelta] = useState('');
  const computedDelta = amount ? Math.floor(parseFloat(amount)) : (delta ? parseInt(delta, 10) : 0);
  return (
    <Modal onClose={onClose} title={`Créditer ${customer.name}`}>
      <div className="bg-[#FDFAF4] rounded-lg p-3 mb-4 text-sm">
        <p>Solde actuel : <strong className="text-[#1E7A6A]">{customer.points} pts</strong></p>
        <p className="text-xs text-[#332724]/70">Cumul dépensé : {Number(customer.total_spent || 0).toFixed(2)}€</p>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(customer.id, computedDelta, amount ? parseFloat(amount) : null); }} className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-[#332724]/70">Montant achat (€) — calcule auto les points</label>
          <input type="number" step="0.01" min="0" placeholder="Ex : 25.00" value={amount} onChange={(e) => { setAmount(e.target.value); setDelta(''); }} className="w-full p-2.5 border border-[#B8922A]/30 rounded-lg text-sm mt-1" data-testid="loyalty-credit-amount" />
        </div>
        <p className="text-center text-xs text-[#332724]/60 italic">— ou —</p>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-[#332724]/70">Ajuster manuellement (positif ou négatif)</label>
          <input type="number" placeholder="Ex : +50 ou -10" value={delta} onChange={(e) => { setDelta(e.target.value); setAmount(''); }} className="w-full p-2.5 border border-[#B8922A]/30 rounded-lg text-sm mt-1" />
        </div>
        {computedDelta !== 0 && (
          <p className="text-sm text-center bg-[#1E7A6A]/10 border border-[#1E7A6A]/30 rounded-lg p-2.5">
            → <strong className="text-[#1E7A6A]">{computedDelta > 0 ? '+' : ''}{computedDelta} pts</strong>
            {' · '}Nouveau solde : <strong>{Math.max(0, customer.points + computedDelta)} pts</strong>
          </p>
        )}
        <button type="submit" disabled={computedDelta === 0} className="w-full btn-primary px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] disabled:opacity-50" data-testid="loyalty-credit-submit">
          Confirmer
        </button>
      </form>
    </Modal>
  );
};

const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#332724]/60 backdrop-blur-sm" onClick={onClose}>
    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute top-3 right-3 text-[#332724]/50 hover:text-[#5C2018]"><X size={18} /></button>
      <h2 className="text-xl text-[#5C2018] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>{title}</h2>
      {children}
    </div>
  </div>
);

export default AdminLoyalty;
